import { MOMENT_TYPES } from 'utils/constants';
import { formatMonthlyItemDisplayTitle } from './formatMonthlyItemDisplayTitle';
import { getMonthlyTimelineDisplayDates } from './getMonthlyBadgeVariant';
import { resolveItemDayPosition } from './resolveItemDayPosition';
import type { MonthDescriptor, MonthlyTimelineItem } from './types';

/** Matches SearchField `SearchResult` shape for dropdown rendering. */
export interface MonthlySearchResult {
  id: string;
  title: string;
  type: 'main' | 'child';
  parentId?: string;
  parentTitle?: string;
  productGroups?: string;
  categories?: string[];
  momentType?: string;
  isInTheWorks?: boolean;
  releasePhase?: string;
  releasePhaseId?: string;
  showOnTimeline?: boolean;
  tier?: string;
  targetDate?: string;
  gaDate?: string;
}

export interface MonthlySearchIndexEntry {
  item: MonthlySearchResult;
  searchText: string;
  /** searchText pre-split into words, so per-keystroke search does not re-split every item. */
  searchWords: string[];
}

/** An entry before the index pass attaches {@link MonthlySearchIndexEntry.searchWords}. */
type MonthlySearchIndexDraft = Omit<MonthlySearchIndexEntry, 'searchWords'>;

export interface MonthlySearchIndex {
  items: MonthlySearchIndexEntry[];
  wordIndex: Map<string, number[]>;
}

function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[^\w\s:\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getTitle(item: { title?: string; name?: string }): string {
  return item.title || item.name || '';
}

function getMonthlyTargetDateForSearch(
  item: MonthlyTimelineItem
): string | undefined {
  const displayDates = getMonthlyTimelineDisplayDates(item);

  if (displayDates.start) return displayDates.start;
  if (displayDates.month) return displayDates.month;
  if (displayDates.end) return displayDates.end;
  if (displayDates.quarter) return displayDates.quarter;

  return undefined;
}

function getMonthlyGaDateForSearch(
  item: MonthlyTimelineItem
): string | undefined {
  const displayDates = getMonthlyTimelineDisplayDates(item);

  if (displayDates.start) return displayDates.start;
  if (displayDates.month) return displayDates.month;
  if (displayDates.quarter) return displayDates.quarter;

  return (
    item.dates?.effectiveDate ||
    item.dates?.effectiveMonth ||
    item.dates?.effectiveQuarter ||
    undefined
  );
}

function isVisibleOnMonthlyTimeline(
  item: MonthlyTimelineItem,
  months: MonthDescriptor[]
): boolean {
  if (!months.length) return false;
  return resolveItemDayPosition(item, months) !== null;
}

function buildTbdItemIds(releases: any): Set<string> {
  const ids = new Set<string>();

  if (!releases?.tbd || !Array.isArray(releases.tbd)) {
    return ids;
  }

  releases.tbd.forEach((tbdSection: any) => {
    if (!tbdSection.project || !Array.isArray(tbdSection.project)) return;

    tbdSection.project.forEach((tbdItem: any) => {
      if (tbdItem.issueId) {
        const compositeKey = tbdItem.releasePhaseId
          ? `${tbdItem.issueId}-${tbdItem.releasePhaseId}`
          : tbdItem.issueId;
        ids.add(compositeKey);
      }

      tbdItem.childRelationships?.forEach((child: any) => {
        if (child.issueId) ids.add(child.issueId);
      });

      tbdItem.features?.forEach((feature: any) => {
        const featureUniqueId = feature.featureId || feature.issueId;
        if (featureUniqueId) ids.add(featureUniqueId);
      });
    });
  });

  return ids;
}

/**
 * Builds a search index scoped to items visible in the monthly timeline (or parking lot).
 * Mirrors quarterly SearchField indexing but filters to monthly-view items and uses
 * monthly date/title formatting.
 */
export function buildMonthlySearchIndex(
  releases: any,
  months: MonthDescriptor[]
): MonthlySearchIndex {
  const allSearchableItems: MonthlySearchIndexDraft[] = [];

  if (!releases?.releaseScheduled || !months.length) {
    return { items: [], wordIndex: new Map<string, number[]>() };
  }

  const tbdItemIds = buildTbdItemIds(releases);

  releases.releaseScheduled.forEach((release: any) => {
    release.projects?.forEach((project: any) => {
      project.project?.forEach((subProject: any) => {
        const compositeKey = subProject.releasePhaseId
          ? `${subProject.issueId}-${subProject.releasePhaseId}`
          : subProject.issueId;

        if (tbdItemIds.has(compositeKey)) {
          return;
        }

        if (!isVisibleOnMonthlyTimeline(subProject, months)) {
          return;
        }

        const projectTitle = getTitle(subProject);

        if (projectTitle) {
          const isInTheWorks = !!(
            subProject.dates?.targetDateStatus === 'TBD' ||
            subProject.dates?.releaseDateStatus === 'TBD' ||
            subProject.dates?.effectiveDateStatus === 'TBD'
          );

          allSearchableItems.push({
            item: {
              id: subProject.issueId,
              title: formatMonthlyItemDisplayTitle(subProject),
              type: 'main',
              productGroups: project.productGroups,
              categories: project.categories,
              momentType:
                subProject.momentType ||
                subProject.projectType ||
                MOMENT_TYPES.PRODUCT_RELEASE,
              releasePhase: subProject.releasePhase,
              releasePhaseId: subProject.releasePhaseId,
              showOnTimeline: subProject.showOnTimeline,
              isInTheWorks,
              tier: subProject.priority || subProject.tier,
              targetDate: getMonthlyTargetDateForSearch(subProject)
            },
            searchText: normalizeSearchText(projectTitle)
          });
        }

        subProject.childRelationships?.forEach((child: any) => {
          if (tbdItemIds.has(child.issueId)) return;

          const childTitle = getTitle(child);
          if (!childTitle) return;

          allSearchableItems.push({
            item: {
              id: child.issueId,
              title: formatMonthlyItemDisplayTitle(child),
              type: 'child',
              parentId: subProject.issueId,
              parentTitle: projectTitle,
              productGroups: project.productGroups,
              categories: project.categories,
              momentType:
                child.momentType ||
                child.projectType ||
                MOMENT_TYPES.PRODUCT_RELEASE,
              tier:
                child.priority ||
                child.tier ||
                subProject.priority ||
                subProject.tier,
              gaDate: getMonthlyGaDateForSearch(child)
            },
            searchText: normalizeSearchText(childTitle)
          });

          child.childRelationships?.forEach((grandChild: any) => {
            if (tbdItemIds.has(grandChild.issueId)) return;

            const grandChildTitle = getTitle(grandChild);
            if (!grandChildTitle) return;

            allSearchableItems.push({
              item: {
                id: grandChild.issueId,
                title: formatMonthlyItemDisplayTitle(grandChild),
                type: 'child',
                parentId: child.issueId,
                parentTitle: childTitle,
                productGroups: project.productGroups,
                categories: project.categories,
                momentType:
                  grandChild.momentType ||
                  grandChild.projectType ||
                  MOMENT_TYPES.FEATURE,
                tier:
                  grandChild.priority ||
                  grandChild.tier ||
                  child.priority ||
                  child.tier ||
                  subProject.priority ||
                  subProject.tier
              },
              searchText: normalizeSearchText(grandChildTitle)
            });
          });
        });

        subProject.features?.forEach((feature: any) => {
          const featureUniqueId = feature.featureId || feature.issueId;
          if (tbdItemIds.has(featureUniqueId)) return;

          const featureTitle = getTitle(feature);
          if (!featureTitle) return;

          allSearchableItems.push({
            item: {
              id: featureUniqueId,
              title: featureTitle,
              type: 'child',
              parentId: subProject.issueId,
              parentTitle: projectTitle,
              productGroups: project.productGroups,
              categories: project.categories,
              momentType: MOMENT_TYPES.FEATURE,
              releasePhase: subProject.releasePhase,
              releasePhaseId: subProject.releasePhaseId,
              showOnTimeline: subProject.showOnTimeline,
              tier: subProject.priority || subProject.tier,
              gaDate: getMonthlyGaDateForSearch(feature)
            },
            searchText: normalizeSearchText(featureTitle)
          });
        });
      });
    });
  });

  if (releases.tbd && Array.isArray(releases.tbd)) {
    releases.tbd.forEach((tbdSection: any) => {
      if (!tbdSection.project || !Array.isArray(tbdSection.project)) return;

      tbdSection.project.forEach((tbdItem: any) => {
        const tbdTitle = getTitle(tbdItem);
        if (!tbdTitle) return;

        allSearchableItems.push({
          item: {
            id: tbdItem.issueId,
            title: formatMonthlyItemDisplayTitle(tbdItem),
            type: 'main',
            productGroups: tbdItem.productGroups || tbdSection.productGroups,
            categories: tbdItem.categories || tbdSection.categories,
            momentType:
              tbdItem.momentType ||
              tbdItem.projectType ||
              MOMENT_TYPES.PRODUCT_RELEASE,
            isInTheWorks: true,
            releasePhase: tbdItem.releasePhase,
            releasePhaseId: tbdItem.releasePhaseId,
            showOnTimeline: tbdItem.showOnTimeline || false,
            tier: tbdItem.priority || tbdItem.tier,
            targetDate: getMonthlyTargetDateForSearch(tbdItem)
          },
          searchText: normalizeSearchText(tbdTitle)
        });

        tbdItem.childRelationships?.forEach((child: any) => {
          const childTitle = getTitle(child);
          if (!childTitle) return;

          allSearchableItems.push({
            item: {
              id: child.issueId,
              title: formatMonthlyItemDisplayTitle(child),
              type: 'child',
              parentId: tbdItem.issueId,
              parentTitle: tbdTitle,
              productGroups: tbdItem.productGroups || tbdSection.productGroups,
              categories: tbdItem.categories || tbdSection.categories,
              momentType:
                child.momentType ||
                child.projectType ||
                MOMENT_TYPES.PRODUCT_RELEASE,
              isInTheWorks: true,
              tier:
                child.priority ||
                child.tier ||
                tbdItem.priority ||
                tbdItem.tier,
              gaDate: getMonthlyGaDateForSearch(child)
            },
            searchText: normalizeSearchText(childTitle)
          });
        });

        tbdItem.features?.forEach((feature: any) => {
          const featureUniqueId = feature.featureId || feature.issueId;
          const featureTitle = getTitle(feature);
          if (!featureTitle) return;

          allSearchableItems.push({
            item: {
              id: featureUniqueId,
              title: featureTitle,
              type: 'child',
              parentId: tbdItem.issueId,
              parentTitle: tbdTitle,
              productGroups: tbdItem.productGroups || tbdSection.productGroups,
              categories: tbdItem.categories || tbdSection.categories,
              momentType: MOMENT_TYPES.FEATURE,
              isInTheWorks: true,
              tier: tbdItem.priority || tbdItem.tier,
              gaDate: getMonthlyGaDateForSearch(feature)
            },
            searchText: normalizeSearchText(featureTitle)
          });
        });
      });
    });
  }

  const wordIndex = new Map<string, number[]>();

  // searchWords is attached in the same pass so the split happens once here, not per keystroke.
  const indexedItems = allSearchableItems.map((entry, index) => {
    const words = entry.searchText.split(/\s+/);
    words.forEach(word => {
      if (word.length >= 2) {
        if (!wordIndex.has(word)) {
          wordIndex.set(word, []);
        }
        wordIndex.get(word)!.push(index);
      }
    });
    return { ...entry, searchWords: words };
  });

  return { items: indexedItems, wordIndex };
}
